import { classes } from "lib/actions/util";
import { useState, useEffect } from "react";
import { useSpeckleAutomatedProject } from "lib/hooks/useSpeckleAutomatedProject";
import { useCurrentProjectId } from "lib/hooks/useProject";
import { Switch } from "@headlessui/react";
import { CreateSpeckleAutomatedProjectModel } from "./types/CreateSpeckleAutomatedProjectModel";
import { ToggleSpeckleAutomatedProjectModel } from "./types/ToggleSpeckleAutomatedProjectModel";

export default function SpeckleAutomateToggle({
  connectedSpeckleAccountId,
  speckleProjectId,
}: {
  connectedSpeckleAccountId: number;
  speckleProjectId: string;
}) {
  const projectId = useCurrentProjectId();
  const {
    automatedProject,
    isLoading,
    error,
    createSpeckleAutomatedProject,
    toggleSpeckleAutomatedProject,
    isUserSpeckleProjectOwner,
  } = useSpeckleAutomatedProject(
    projectId,
    connectedSpeckleAccountId,
    speckleProjectId,
  );
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (automatedProject !== undefined) {
      setEnabled(automatedProject?.enabled || false);
    }
  }, [automatedProject]);

  const handleToggle = async () => {
    if (!enabled && automatedProject === null) {
      const createModel: CreateSpeckleAutomatedProjectModel = {
        projectId: projectId,
        speckleConnectedAccountId: connectedSpeckleAccountId,
        connectedSpeckleProjectId: speckleProjectId,
      };
      await createSpeckleAutomatedProject(createModel);
    } else {
      const toggleModel: ToggleSpeckleAutomatedProjectModel = {
        Id: automatedProject.id,
        speckleConnectedAccountId: automatedProject.speckleConnectedAccountId,
      };
      await toggleSpeckleAutomatedProject(toggleModel);
    }
  };

  if (isLoading) return <p>Loading...</p>;
  if (error) return <p>Error loading data</p>;
  if (!isUserSpeckleProjectOwner) return null;

  return (
    <div className="flex items-center">
      <Switch
        checked={enabled}
        onChange={handleToggle}
        className={classes(
          "relative inline-flex h-6 w-11 flex-shrink-0",
          "cursor-pointer rounded-full border-2 border-transparent",
          "transition-colors duration-200 ease-in-out",
          enabled ? "bg-[#047efb]" : "bg-gray-200",
        )}
      >
        <span
          aria-hidden="true"
          className={classes(
            "pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0",
            "transform transition duration-200 ease-in-out",
            enabled ? "translate-x-5" : "translate-x-0",
          )}
        />
      </Switch>
      <div className="ml-3 text-sm">
        <span className="font-medium text-gray-900">
          Enable automatic import of models
        </span>
      </div>
    </div>
  );
}
